// src/results/Results.jsx
import React from "react";
import {
  Box,
  Tabs,
  Tab,
  Container,
  Stack,
  Chip,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Grid,
} from "@mui/material";
import { ACC_FILES, AC_FILES, AC_EVO_FILES } from "./files.config";
import { aceCompositeId, toAppDriverId } from "../utils/aceResults";
import RaceView from "../race/RaceView";
import { useSearchParams } from "react-router-dom";

/**
 * ===============
 * Utilities
 * ===============
 */
const msToLap = (ms) => {
  if (!Number.isFinite(ms) || ms <= 0 || ms === 2147483647 || ms === 999999999) return "—";
  const totalMs = Math.floor(ms);
  const minutes = Math.floor(totalMs / 60000);
  const seconds = Math.floor((totalMs % 60000) / 1000);
  const millis = totalMs % 1000;
  return `${minutes}:${seconds.toString().padStart(2, "0")}.${millis
    .toString()
    .padStart(3, "0")}`;
};

const safe = (x, fallback = "—") =>
  x === null || x === undefined || x === "" ? fallback : x;

function pretty(obj, space = 2) {
  try {
    return JSON.stringify(obj, null, space);
  } catch {
    return String(obj);
  }
}

/**
 * Parse date/time from file names and render a nice local label.
 * AC format:  2025_10_18_12_20_PRACTICE.json  => YYYY_MM_DD_HH_mm_TYPE
 * ACC format: 251018_120352_Q.json            => DDMMYY_HHMMSS_TYPE
 */
function parseDateFromFilename(name) {
  if (!name) return null;

  // Normalize just the basename
  const base = String(name).split("/").pop().replace(".json", "");

  // AC pattern
  const acMatch = base.match(/^(\d{4})_(\d{2})_(\d{2})_(\d{2})_(\d{2})_/);
  if (acMatch) {
    const [_, Y, M, D, h, m] = acMatch;
    const dt = new Date(Number(Y), Number(M) - 1, Number(D), Number(h), Number(m), 0);
    return isNaN(dt.getTime()) ? null : dt;
  }

  // ACC pattern
  const accMatch = base.match(/^(\d{2})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})_/);
  if (accMatch) {
    const [_, y, m, d, hh, mm, ss] = accMatch;
    // Assume 20xx for YY < 80 (rough rule of thumb)
    const fullY = Number(y) + (Number(y) < 80 ? 2000 : 1900);
    const dt = new Date(fullY, Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss));
    return isNaN(dt.getTime()) ? null : dt;
  }

  // Assetto Corsa EVO pattern: results_YYYYMMDD_HHmmss_TYPE.json
  const aceMatch = base.match(/^results_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})_/i);
  if (aceMatch) {
    const [_, Y, M, D, h, m, s] = aceMatch;
    const dt = new Date(Number(Y), Number(M) - 1, Number(D), Number(h), Number(m), Number(s));
    return isNaN(dt.getTime()) ? null : dt;
  }

  return null;
}

function formatDate(dt) {
  if (!(dt instanceof Date) || isNaN(dt.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "long",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(dt);
  } catch {
    return dt.toLocaleString();
  }
}

function stripBom(s) {
  return s && s.charCodeAt(0) === 0xfeff ? s.slice(1) : s;
}
function isGzip(u8) {
  return u8 && u8.length > 2 && u8[0] === 0x1f && u8[1] === 0x8b; // 1F 8B
}
async function gunzipIfSupported(buf) {
  if (typeof DecompressionStream === "function") {
    const ds = new DecompressionStream("gzip");
    const stream = new Response(buf).body.pipeThrough(ds);
    return await new Response(stream).arrayBuffer();
  }
  return buf;
}
function tryParseWithDecoders(buf) {
  const decoders = ["utf-8", "utf-16le", "utf-16be"];
  for (const enc of decoders) {
    try {
      const txt = new TextDecoder(enc, { fatal: false }).decode(buf);
      return JSON.parse(stripBom(txt));
    } catch {
      /* try next */
    }
  }
  throw new Error("Unrecognized encoding");
}

/**
 * ===============================
 * Local cache for fetched JSON
 * ===============================
 */
const __jsonPromiseCache = new Map(); // url -> Promise<any>
export function clearResultsFetchCache() {
  __jsonPromiseCache.clear();
}

async function fetchJsonUncached(url) {
  try {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    let buf = await res.arrayBuffer();
    let u8 = new Uint8Array(buf);

    if (isGzip(u8)) {
      try {
        buf = await gunzipIfSupported(buf);
        u8 = new Uint8Array(buf);
      } catch (e) {
        return { __error: `Gzip decode failed: ${e.message}` };
      }
    }

    try {
      return tryParseWithDecoders(buf);
    } catch (e) {
      return { __error: `Unrecognized encoding: ${e.message}` };
    }
  } catch (e) {
    return { __error: e.message || String(e) };
  }
}

async function fetchJson(url) {
  const key = String(url);

  if (__jsonPromiseCache.has(key)) {
    return __jsonPromiseCache.get(key);
  }

  const p = (async () => {
    const json = await fetchJsonUncached(key);
    return json;
  })();

  __jsonPromiseCache.set(key, p);

  p.catch(() => {
    __jsonPromiseCache.delete(key);
  });

  return p;
}

/**
 * ===========================
 * Parsing: Assetto Corsa (AC)
 * ===========================
 */
function parseAC(json) {
  if (!json || json.__error) {
    return { ok: false, error: json?.__error || "Failed to load AC JSON" };
  }

  const track = json.TrackName || "unknown";
  const type = json.Type || "SESSION";
  const cars = Array.isArray(json.Cars) ? json.Cars : [];
  const results = Array.isArray(json.Result) ? json.Result : [];
  const laps = Array.isArray(json.Laps) ? json.Laps : [];
  const events = Array.isArray(json.Events) ? json.Events : [];

  // Count laps per driver from the Laps array
  const lapCounts = {};
  laps.forEach((lap) => {
    const guid = lap.DriverGuid;
    if (guid) {
      lapCounts[guid] = (lapCounts[guid] || 0) + 1;
    }
  });

  const leaderboard = results
    .map((r) => ({
      driver: r.DriverName || "",
      guid: r.DriverGuid || "",
      carModel: r.CarModel || "",
      carId: r.CarId,
      bestLap: msToLap(r.BestLap),
      bestLapMs: r.BestLap,
      totalTime: msToLap(r.TotalTime),
      totalTimeMs: r.TotalTime,
      ballast: r.BallastKG ?? 0,
      restrictor: r.Restrictor ?? 0,
      lapCount: lapCounts[r.DriverGuid] || 0,
    }))
    .sort((a, b) => {
      if (b.lapCount !== a.lapCount) return b.lapCount - a.lapCount;
      return a.totalTimeMs - b.totalTimeMs;
    })
    .map((r, idx) => ({
      pos: idx + 1,
      ...r,
    }));

  const bestLapMs = leaderboard
    .map((x) => x.bestLapMs)
    .filter((x) => Number.isFinite(x) && x > 0 && x < 999999999);
  const overallBest = bestLapMs.length ? Math.min(...bestLapMs) : null;
  const overallBestDriver = leaderboard.find((r) => r.bestLapMs === overallBest);
  return {
    ok: true,
    game: "AC",
    summary: {
      track,
      type,
      entries: cars.length || leaderboard.length,
      overallBestLap: overallBest ? msToLap(overallBest) : "—",
      overallBestDriver: overallBestDriver ? overallBestDriver.driver : "—",
      carsCount: cars.length,
      resultsCount: results.length,
      lapsCount: laps.length,
      eventsCount: events.length,
    },
    cars,
    leaderboard,
    laps,
    events,
    raw: json,
  };
}

/**
 * ==========================================
 * Parsing: Assetto Corsa Competizione (ACC)
 * ==========================================
 */
function parseACC(json) {
  if (!json || json.__error) {
    return { ok: false, error: json?.__error || "Failed to load ACC JSON" };
  }

  const typeCode = json.sessionType || "UNK"; // FP/Q/R
  const typeMap = { R: "Race", Q: "Qualify", FP: "Practice" };
  const type = typeMap[typeCode] || typeCode;

  const track = json.trackName || json.metaData || "unknown";
  const sr = json.sessionResult || {};
  const lines = Array.isArray(sr.leaderBoardLines) ? sr.leaderBoardLines : [];

  const leaderboard = lines.map((l, i) => {
    const car = l.car || {};
    const drv = (car.drivers && car.drivers[0]) || l.currentDriver || {};
    const timing = l.timing || {};

    const carModelId = car.carModel;
    const carModel =
      (Number.isFinite(carModelId) ? `Car #${carModelId}` : "—");

    return {
      pos: i + 1,
      raceNumber: car.raceNumber,
      driver: [drv.firstName, drv.lastName].filter(Boolean).join(" ") || drv.shortName || "—",
      playerId: drv.playerId || "—",
      carModel,
      carModelId,
      cup: car.cupCategory,
      group: car.carGroup,
      bestLap: msToLap(timing.bestLap),
      bestLapMs: timing.bestLap,
      lastLap: msToLap(timing.lastLap),
      lastSplits: timing.lastSplits || [],
      bestSplits: timing.bestSplits || [],
      totalTime: msToLap(timing.totalTime),
      lapCount: timing.lapCount ?? 0,
    };
  });

  const bestLapMs = leaderboard
    .map((x) => x.bestLapMs)
    .filter((x) => Number.isFinite(x) && x > 0 && x < 2147483647);
  const overallBest = bestLapMs.length ? Math.min(...bestLapMs) : null;

  const laps = Array.isArray(json.laps) ? json.laps : [];
  const penalties = Array.isArray(json.penalties) ? json.penalties : [];
  const postPenalties = Array.isArray(json.post_race_penalties) ? json.post_race_penalties : [];

  return {
    ok: true,
    game: "ACC",
    summary: {
      track,
      type,
      entries: leaderboard.length,
      overallBestLap: overallBest ? msToLap(overallBest) : "—",
      overallBestDriver: leaderboard.find((r) => r.bestLapMs === overallBest)?.driver || "—",
      isWet: sr.isWetSession === 1,
      sessionIndex: json.sessionIndex,
      raceWeekendIndex: json.raceWeekendIndex,
      serverName: json.serverName || "",
      bestLap: sr.bestlap ? msToLap(sr.bestlap) : "—",
      bestSplits: Array.isArray(sr.bestSplits) ? sr.bestSplits : [],
      lapsCount: laps.length,
      penaltiesCount: penalties.length,
      postPenaltiesCount: postPenalties.length,
    },
    leaderboard,
    laps,
    penalties,
    postPenalties,
    raw: json,
  };
}

function parseACE(json) {
  if (!json || json.__error) {
    return { ok: false, error: json?.__error || "Failed to load ACE JSON" };
  }

  const drivers = Array.isArray(json.drivers) ? json.drivers : [];
  const cars = Array.isArray(json.cars) ? json.cars : [];
  const standings = Array.isArray(json.driver_standings) ? json.driver_standings : [];
  const rawLaps = Array.isArray(json.laps) ? json.laps : [];
  const driversByGuid = new Map(drivers.map((driver) => [aceCompositeId(driver.guid), driver]));
  const carsById = new Map(cars.map((car) => [aceCompositeId(car.car_id), car]));
  const carByDriver = new Map();
  rawLaps.forEach((lap) => {
    const driverGuid = aceCompositeId(lap.driver_key);
    if (driverGuid && !carByDriver.has(driverGuid)) carByDriver.set(driverGuid, aceCompositeId(lap.car_key));
  });

  const leaderboard = standings.map((standing, index) => {
    const guid = aceCompositeId(standing);
    const driver = driversByGuid.get(guid) || {};
    const car = carsById.get(carByDriver.get(guid)) || cars[index] || {};
    const driverLaps = rawLaps.filter((lap) => aceCompositeId(lap.driver_key) === guid);
    const validTimes = driverLaps.map((lap) => lap.time).filter((time) => Number.isFinite(time) && time > 0);
    const bestLapMs = validTimes.length ? Math.min(...validTimes) : null;
    const lastLap = driverLaps[driverLaps.length - 1];
    return {
      pos: index + 1,
      raceNumber: car.race_number,
      driver: [driver.first_name, driver.last_name].filter(Boolean).join(" ") || driver.nickname || "Driver",
      playerId: toAppDriverId(driver.player_id),
      carModel: car.model_displayname || "â€”",
      bestLap: msToLap(bestLapMs),
      bestLapMs,
      lastLap: msToLap(lastLap?.time),
      lastSplits: lastLap?.split || [],
      bestSplits: driverLaps.find((lap) => lap.time === bestLapMs)?.split || [],
      totalTime: msToLap(json.time_standings?.[index]),
      totalTimeMs: json.time_standings?.[index],
      lapCount: driverLaps.length,
    };
  });
  const overallBest = leaderboard.map((row) => row.bestLapMs).filter(Number.isFinite).sort((a, b) => a - b)[0];

  return {
    ok: true,
    game: "ACE",
    summary: {
      track: [json.track_name, json.track_layout_name].filter(Boolean).join(" — ") || "unknown",
      type: normalizeSessionType(json.session_type),
      entries: leaderboard.length,
      overallBestLap: msToLap(overallBest),
      overallBestDriver: leaderboard.find((row) => row.bestLapMs === overallBest)?.driver || "â€”",
      lapsCount: rawLaps.length,
    },
    leaderboard,
    laps: rawLaps.map((lap) => {
      const driver = driversByGuid.get(aceCompositeId(lap.driver_key)) || {};
      const car = carsById.get(aceCompositeId(lap.car_key)) || {};
      return {
        carId: car.race_number,
        driverIndex: [driver.first_name, driver.last_name].filter(Boolean).join(" ") || driver.nickname || "â€”",
        laptime: lap.time,
        splits: lap.split || [],
        isValidForBest: (lap.flags & 4) === 0,
      };
    }),
    penalties: [],
    raw: json,
  };
}

/**
 * ======
 * UI
 * ======
 */

function normalizeSessionType(type) {
  const t = String(type || "").toLowerCase();
  if (t.startsWith("practice") || t === "fp") return "Practice";
  if (t.startsWith("qual") || t === "q") return "Qualify";
  if (t.startsWith("race") || t === "r") return "Race";
  return type || "Session";
}

/**
 * Group sessions (AC or ACC) into events by track and calendar day.
 * Each event can hold up to one Practice, Qualify and Race session.
 */
function groupSessionsByEvent(sessions = []) {
  const groups = new Map();

  sessions.forEach((session) => {
    if (!session) return;
    const dt = parseDateFromFilename(session.fileName);
    const dateKey = dt ? dt.toISOString().slice(0, 10) : "unknown";
    const track = safe(session?.summary?.track, "Track");
    const eventName = safe(session?.summary?.serverName, "Race Event");
    const key = `${track}|${dateKey}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        track,
        eventName,
        date: dt || null,
        sessions: {},
      });
    }

    const group = groups.get(key);
    if (dt && (!group.date || dt < group.date)) {
      group.date = dt;
    }

    const label = normalizeSessionType(session?.summary?.type);
    group.sessions[label] = session;
  });

  return Array.from(groups.values()).sort((a, b) => {
    if (a.date && b.date) return b.date - a.date;
    if (a.date) return -1;
    if (b.date) return 1;
    return String(b.key).localeCompare(String(a.key));
  });
}

function eventLabel(event) {
  if (!event) return "Event";
  const dt = event.date ? formatDate(event.date) : null;
  let tr = safe(event.track, "Track");
  tr = tr.charAt(0).toUpperCase() + tr.slice(1);
  tr = tr.replace(/_/g, " ");
  let wordsd = tr.split(" ");
  tr = wordsd.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  let eventName = safe(event.eventName, "Race Event");
  eventName = eventName.split("-");
  eventName = eventName[eventName.length - 1].trim();
  let season = "Test Season";
  if (dt && String(dt).includes("2026")) season = "Season 1";
  return `${season} • ${eventName} • ${tr}`;
}

function sessionLabel(d) {
  if (!d) return "Session";
  // camel case (capatilise the first letter)
  let tr = safe(d?.summary?.track, "Track").replace(/^\w/, (c) => c.toUpperCase());
  tr = tr.replace(/_/g, " ");
  let wordsd = tr.split(" ");
  tr = wordsd.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  return `${tr}`;
}

function sessionDate(event) {
  if (!event) return "";
  const dt = event.date ? formatDate(event.date) : null;
  return `${dt ? `${dt}` : ""}`;
}

/**
 * ===================
 * Event Card (mobile-first, desktop matches)
 * ===================
 */
function EventRowCard({ event, onOpenSession }) {
  const practice = event.sessions["Practice"];
  const qualify = event.sessions["Qualify"];
  const race = event.sessions["Race"];

  const entries =
    (race && race.summary && race.summary.entries) ??
    (qualify && qualify.summary && qualify.summary.entries) ??
    (practice && practice.summary && practice.summary.entries) ??
    0;

  const bestLap =
    (race && race.summary && race.summary.overallBestLap) ??
    (qualify && qualify.summary && qualify.summary.overallBestLap) ??
    (practice && practice.summary && practice.summary.overallBestLap) ??
    "—";

  const bestDriver =
    (race && race.summary && race.summary.overallBestDriver) ??
    (qualify && qualify.summary && qualify.summary.overallBestDriver) ??
    (practice && practice.summary && practice.summary.overallBestDriver) ??
    "—";

  const dt = event.date ? formatDate(event.date) : null;

  return (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        width: "100%",
        display: "flex",
        background: "linear-gradient(180deg, rgba(0,12,32,0.3) 0%, rgba(0,12,32,0.8) 100%)",
        cursor: "pointer"
      }}
    >
      <Box
        sx={{ p: 1.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{ minWidth: 0, flex: 1 }}
          >
            <Typography sx={{ fontWeight: 900, p:1 }} >
              {eventLabel(event)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{p:1}}>
              {sessionDate(event)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{p:1}}>
              Entries: {safe(entries, 0)} • Best Lap: {safe(bestLap, "—")} - {safe(bestDriver, "—")}
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: "wrap", rowGap: 0.75, p:1 }}>
              {race ? (
                <Chip
                  size="large"
                  clickable
                  label="Race"
                  variant="outlined"
                  onClick={() => onOpenSession(race)}
                />
              ) : (
                <Chip size="large" label="Race" variant="outlined" disabled />
              )}
              {qualify ? (
                <Chip
                  size="large"
                  clickable
                  label="Qualify"
                  variant="outlined"
                  onClick={(e) => {
                    onOpenSession(qualify)
                  }}
                />
              ) : (
                <Chip size="large" label="Qualify" variant="outlined" disabled />
              )}
              {practice ? (
                <Chip
                  size="large"
                  clickable
                  label="Practice"
                  variant="outlined"
                  onClick={() => onOpenSession(practice)}
                />
              ) : (
                <Chip size="large" label="Practice" variant="outlined" disabled />
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
}

/**
 * ===================
 * Main Results page
 * ===================
 */
export default function Results({ filesAC, filesACC, filesACE, data }) {
  const [searchParams] = useSearchParams();
  const requestedSession = searchParams.get("session");
  const [tab, setTab] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [acData, setAcData] = React.useState([]);
  const [accData, setAccData] = React.useState([]);
  const [aceData, setAceData] = React.useState([]);
  const [errors, setErrors] = React.useState([]);

  const [open, setOpen] = React.useState(false);
  const [selectedSession, setSelectedSession] = React.useState(null);

  const groupedAccEvents = React.useMemo(() => groupSessionsByEvent(accData), [accData]);
  const groupedAcEvents = React.useMemo(() => groupSessionsByEvent(acData), [acData]);
  const groupedAceEvents = React.useMemo(() => groupSessionsByEvent(aceData), [aceData]);

  const acFiles = filesAC && filesAC.length ? filesAC : AC_FILES;
  const accFiles = filesACC && filesACC.length ? filesACC : ACC_FILES;
  const aceFiles = filesACE && filesACE.length ? filesACE : AC_EVO_FILES;

  const depKey = React.useMemo(
    () => `${acFiles.join("|")}||${accFiles.join("|")}||${aceFiles.join("|")}`,
    [acFiles, accFiles, aceFiles]
  );

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      const _errors = [];
      try {
        const acResults = [];
        for (const f of acFiles) {
          const url = process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/${f}` : `/${f}`;
          const j = await fetchJson(url);
          if (j.__error) {
            _errors.push(`AC ${f}: ${j.__error}`);
            acResults.push({ ok: false, error: j.__error, fileName: f });
          } else {
            acResults.push({ ...parseAC(j), fileName: f });
          }
        }

        const accResults = [];
        for (const f of accFiles) {
          const url = process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/${f}` : `/${f}`;
          const j = await fetchJson(url);
          if (j.__error) {
            _errors.push(`ACC ${f}: ${j.__error}`);
            accResults.push({ ok: false, error: j.__error, fileName: f });
          } else {
            accResults.push({ ...parseACC(j), fileName: f });
          }
        }

        const aceResults = [];
        for (const f of aceFiles) {
          const url = process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/${f}` : `/${f}`;
          const j = await fetchJson(url);
          if (j.__error) {
            _errors.push(`ACE ${f}: ${j.__error}`);
            aceResults.push({ ok: false, error: j.__error, fileName: f });
          } else {
            aceResults.push({ ...parseACE(j), fileName: f });
          }
        }

        if (!mounted) return;
        setAcData(acResults);
        setAccData(accResults);
        setAceData(aceResults);
      } catch (e) {
        _errors.push(`Error loading results: ${e.message}`);
      }
      if (mounted) {
        setErrors(_errors);
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [depKey]);

  const handleOpen = (d) => {
    setSelectedSession(d);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setSelectedSession(null);
  };

  React.useEffect(() => {
    if (loading || !requestedSession) return;

    const match = [...accData, ...acData, ...aceData].find(
      (session) => session.fileName === requestedSession && session.ok
    );
    if (!match) return;

    setTab(match.game === "ACC" ? 0 : match.game === "ACE" ? 1 : 2);
    setSelectedSession(match);
    setOpen(true);
  }, [loading, requestedSession, accData, acData, aceData]);

  return (
    <Container sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          Event Results
        </Typography>

        {errors.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Some files had issues:
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              {errors.map((e, i) => (
                <li key={i}>
                  <Typography variant="caption">{e}</Typography>
                </li>
              ))}
            </Box>
          </Alert>
        )}
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Assetto Corsa Competizione" />
          <Tab label="Assetto Corsa EVO" />
          <Tab label="Assetto Corsa" />
        </Tabs>
      </Box>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading sessions…
          </Typography>
        </Stack>
      ) : (
        <>
          {tab === 0 && (
            <Box>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={2}>
                {groupedAccEvents.map((event) => (
                  <Grid key={event.key} item width={"100%"}>
                    <EventRowCard event={event} onOpenSession={handleOpen} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {tab === 2 && (
            <Box>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={2}>
                {groupedAcEvents.map((event) => (
                  <Grid key={event.key} item width={"100%"}>
                    <EventRowCard event={event} onOpenSession={handleOpen} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {tab === 1 && (
            <Box>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={2}>
                {groupedAceEvents.map((event) => (
                  <Grid key={event.key} item width={"100%"}>
                    <EventRowCard event={event} onOpenSession={handleOpen} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </>
      )}

      {/* Modal with RaceView */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        aria-labelledby="raceview-dialog-title"
      >
        <DialogTitle id="raceview-dialog-title">
          {selectedSession ? sessionLabel(selectedSession) : "Session"}
        </DialogTitle>
        <DialogContent dividers>
          {selectedSession && (
            <RaceView
              data={selectedSession}
              fileName={selectedSession.fileName}
              drivers={data?.drivers?.list || []}
              standings={data?.standings?.[selectedSession.game]?.drivers || []}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
